import type { JSX } from 'react'
import Chip, { type ChipProps } from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import type { CompanyStatus, ContactRow, JobStatus } from '@profiler/shared'
import { humanise } from '../format'

/**
 * Every coded value in the product renders through this file.
 *
 * The colours are load-bearing rather than decorative: an amber email chip is
 * the only thing standing between a marketer and mail-merging a founder's
 * university address, which is exactly what the sample file did six times.
 */

type ChipColor = NonNullable<ChipProps['color']>

interface ChipMeta {
  label: string
  color: ChipColor
  title?: string
}

const UNKNOWN: ChipMeta = { label: 'Unknown', color: 'default' }

/** Values arrive as plain strings from the API, so the lookup must tolerate one it has never seen. */
function metaFor<K extends string>(
  map: Record<K, ChipMeta>,
  key: string | null | undefined,
  fallback: ChipMeta = UNKNOWN,
): ChipMeta {
  if (!key) return fallback
  const found = (map as Record<string, ChipMeta | undefined>)[key]
  return found ?? { label: humanise(key), color: fallback.color }
}

function MetaChip({
  meta,
  size = 'small',
}: Readonly<{ meta: ChipMeta; size?: ChipProps['size'] }>): JSX.Element {
  const chip = <Chip size={size} label={meta.label} color={meta.color} variant="outlined" />
  return meta.title ? <Tooltip title={meta.title}>{chip}</Tooltip> : chip
}

// ---------- Job status ------------------------------------------------------

const JOB_STATUS_META: Record<JobStatus, ChipMeta> = {
  DRAFT: { label: 'Draft', color: 'default', title: 'Uploaded but not yet confirmed' },
  READY: { label: 'Ready', color: 'info', title: 'Confirmed and waiting to start' },
  RUNNING: { label: 'Running', color: 'primary' },
  PAUSED: { label: 'Paused', color: 'warning', title: 'Stopped by a credit cap or the kill switch' },
  COMPLETED: { label: 'Completed', color: 'success' },
  COMPLETED_WITH_ERRORS: {
    label: 'Completed with errors',
    color: 'warning',
    title: 'Every company was attempted; some ended in a terminal failure',
  },
  CANCELLED: { label: 'Cancelled', color: 'default' },
  FAILED: { label: 'Failed', color: 'error' },
}

export function JobStatusChip({ status }: Readonly<{ status: JobStatus }>): JSX.Element {
  return <MetaChip meta={metaFor(JOB_STATUS_META, status)} />
}

// ---------- Company status --------------------------------------------------

const COMPANY_STATUS_META: Record<CompanyStatus, ChipMeta> = {
  pending: { label: 'Pending', color: 'default' },
  triaging: { label: 'Triaging', color: 'info', title: 'Resolving the website, zero credits' },
  searching: { label: 'Searching', color: 'info' },
  upserting: { label: 'Saving', color: 'info' },
  done: { label: 'Done', color: 'success' },
  no_people_found: {
    label: 'No people found',
    color: 'warning',
    title: 'The company resolved but no candidate passed the gates',
  },
  company_not_found: {
    label: 'Company not found',
    color: 'warning',
    title: 'Needs a corrected website or LinkedIn URL to continue',
  },
  dead_domain: {
    label: 'Dead domain',
    color: 'warning',
    title: 'The website in the sheet does not resolve. Re-run with a correction.',
  },
  failed: { label: 'Failed', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
}

export function CompanyStatusChip({ status }: Readonly<{ status: CompanyStatus }>): JSX.Element {
  return <MetaChip meta={metaFor(COMPANY_STATUS_META, status)} />
}

// ---------- Domain class ----------------------------------------------------

/** Mirrors DomainClass in backend/src/lib/triage.ts, which is not a shared type. */
const DOMAIN_CLASS_META: Record<string, ChipMeta> = {
  LIVE: { label: 'Live', color: 'success' },
  REDIRECTS_TO: {
    label: 'Redirected',
    color: 'warning',
    title: 'Every lookup used the post-redirect domain, not the sheet value',
  },
  UNRELATED_CONTENT: {
    label: 'Unrelated content',
    color: 'warning',
    title: 'The site resolves but does not look like this company',
  },
  NXDOMAIN: { label: 'NXDOMAIN', color: 'error', title: 'The domain does not exist' },
  NO_HTTP: { label: 'No HTTP', color: 'error', title: 'The domain resolves but serves nothing' },
  TRANSIENT: { label: 'Transient', color: 'default', title: 'Temporary failure, worth a re-run' },
}

export function DomainClassChip({
  domainClass,
}: Readonly<{ domainClass: string | null }>): JSX.Element | null {
  if (!domainClass) return null
  return <MetaChip meta={metaFor(DOMAIN_CLASS_META, domainClass)} />
}

// ---------- Repair ----------------------------------------------------------

/**
 * Where the repair ladder left a company. Retired is red because a person may
 * want to disagree; corrected and renamed are what changed; "sheet was right"
 * is quiet; waiting is the one that asks for somebody.
 */
const REPAIR_META: Record<string, ChipMeta> = {
  retired: {
    label: 'Retired · dead',
    color: 'error',
    title: 'Conclusively dead: registry, DNS and mail agree. Reversible from the Repair tab.',
  },
  corrected: {
    label: 'Corrected',
    color: 'success',
    title: 'A corrected website or LinkedIn page was applied and discovery re-ran.',
  },
  renamed: {
    label: 'Renamed',
    color: 'info',
    title: 'The real name was established; the sheet’s cell is untouched.',
  },
  unchanged: {
    label: 'Sheet was right',
    color: 'default',
    title: 'Examined and found correct; the failure was elsewhere.',
  },
  queued: {
    label: 'Needs a decision',
    color: 'warning',
    title: 'The ladder could not settle it. Waiting on the Repairs page.',
  },
  failed: {
    label: 'Not examinable yet',
    color: 'default',
    title: 'The attempt could not complete (no credits, no key); it will be examined again.',
  },
  rejected: {
    label: 'Repair rejected',
    color: 'default',
    title: 'A person turned the proposal down, or reversed an applied one.',
  },
  claimed: { label: 'Being examined', color: 'default', title: 'A worker is examining it now.' },
}

export function RepairChip({
  repair,
}: Readonly<{ repair: { status: string; verdict: string | null } | null }>): JSX.Element | null {
  if (!repair) return null
  const key = repair.status === 'applied' ? (repair.verdict ?? 'unchanged') : repair.status
  return <MetaChip meta={metaFor(REPAIR_META, key)} />
}

// ---------- Email confidence ------------------------------------------------

/**
 * Green means mail-merge safe. Amber means the address is real but belongs to
 * someone other than this company (alumni and previous-employer addresses were
 * 35% of the sample's emails). Red means never store it.
 */
const EMAIL_CONFIDENCE_META: Record<string, ChipMeta> = {
  company_domain: { label: 'Company domain', color: 'success', title: 'Safe to mail merge' },
  company_alias_domain: {
    label: 'Alias domain',
    color: 'success',
    title: 'A known alias of the company. Safe to mail merge.',
  },
  unverified_academic_domain: {
    label: 'Academic',
    color: 'warning',
    title: 'A university address is never a business contact. Suppressed from mail merge.',
  },
  unverified_domain_mismatch: {
    label: 'Domain mismatch',
    color: 'warning',
    title: 'Belongs to a different organisation. Suppressed from mail merge.',
  },
  unverified_freemail: {
    label: 'Freemail',
    color: 'warning',
    title: 'A personal mailbox provider. Suppressed from mail merge.',
  },
  unverified_no_company_domain: {
    label: 'No company domain',
    color: 'warning',
    title: 'No company domain is known to compare against. Suppressed from mail merge.',
  },
  rejected_disposable: { label: 'Disposable', color: 'error', title: 'Throwaway provider. Rejected.' },
  unknown: { label: 'Unknown', color: 'default' },
}

export function EmailConfidenceChip({
  confidence,
}: Readonly<{ confidence: string | null }>): JSX.Element | null {
  if (!confidence) return null
  return <MetaChip meta={metaFor(EMAIL_CONFIDENCE_META, confidence)} />
}

// ---------- Work email lookup status ---------------------------------------

const WORK_EMAIL_STATUS_META: Record<ContactRow['workEmailStatus'] | 'requested', ChipMeta> = {
  none: { label: 'Not requested', color: 'default' },
  // The phone's mark at request time: asked for, queued, not yet sent.
  requested: {
    label: 'Requested',
    color: 'info',
    title: 'Asked for; the lookup is queued and has not been sent yet',
  },
  pending: { label: 'Pending', color: 'info', title: 'Waiting on the Enrich Layer callback' },
  found: { label: 'Found', color: 'success' },
  not_found: { label: 'Not found', color: 'default' },
  timeout: { label: 'Timed out', color: 'warning', title: 'No callback within 24 hours' },
  error: { label: 'Error', color: 'error' },
}

export function WorkEmailStatusChip({
  status,
}: Readonly<{
  status: ContactRow['workEmailStatus']
}>): JSX.Element {
  return <MetaChip meta={metaFor(WORK_EMAIL_STATUS_META, status)} />
}

/**
 * The same six states as a work email, and deliberately the same words.
 *
 * Two columns that mean the same thing must say it the same way, or the reader
 * has to learn which vocabulary belongs to which column before they can act on
 * either.
 */
export function PhoneStatusChip({ status }: Readonly<{ status: ContactRow['phoneStatus'] }>): JSX.Element {
  return <MetaChip meta={metaFor(WORK_EMAIL_STATUS_META, status ?? 'none')} />
}

// ---------- Seniority -------------------------------------------------------

const SENIORITY_META: Record<string, ChipMeta> = {
  founder: { label: 'Founder', color: 'primary' },
  c_level: { label: 'C-level', color: 'primary' },
  vp: { label: 'VP', color: 'info' },
  head: { label: 'Head', color: 'info' },
  director: { label: 'Director', color: 'info' },
  other: { label: 'Other', color: 'default' },
}

export function SeniorityChip({ band }: Readonly<{ band: string | null }>): JSX.Element | null {
  if (!band) return null
  return <MetaChip meta={metaFor(SENIORITY_META, band)} />
}

// ---------- Outreach --------------------------------------------------------

const OUTREACH_META: Record<ContactRow['outreachStatus'], ChipMeta> = {
  new: { label: 'New', color: 'default' },
  contacted: { label: 'Contacted', color: 'info' },
  replied: { label: 'Replied', color: 'success' },
  not_interested: { label: 'Not interested', color: 'warning' },
  do_not_contact: {
    label: 'Do not contact',
    color: 'error',
    title: 'Suppressed. Excluded from every export that honours suppression.',
  },
}

export function OutreachStatusChip({
  status,
}: Readonly<{
  status: ContactRow['outreachStatus']
}>): JSX.Element {
  return <MetaChip meta={metaFor(OUTREACH_META, status)} />
}

// ---------- Gate outcome ----------------------------------------------------

const GATE_META: Record<string, ChipMeta> = {
  accepted: { label: 'Accepted', color: 'success' },
  needs_review: { label: 'Needs review', color: 'warning' },
  rejected: { label: 'Rejected', color: 'error' },
}

export function GateOutcomeChip({ outcome }: Readonly<{ outcome: string | null }>): JSX.Element | null {
  if (!outcome) return null
  return <MetaChip meta={metaFor(GATE_META, outcome)} />
}

// ---------- Match basis -----------------------------------------------------

const MATCH_BASIS_META: Record<string, ChipMeta> = {
  company_url: { label: 'URL match', color: 'success', title: 'Matched on the company LinkedIn URL' },
  company_domain: { label: 'Domain match', color: 'info' },
  company_name: { label: 'Name match', color: 'warning', title: 'The weakest basis. Country still applies.' },
  none: { label: 'No match', color: 'error' },
}

export function MatchBasisChip({ basis }: Readonly<{ basis: string | null }>): JSX.Element | null {
  if (!basis) return null
  return <MetaChip meta={metaFor(MATCH_BASIS_META, basis)} />
}
